import { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Check } from 'lucide-react'
import PageContainer from '@/components/layout/PageContainer'
import StickySideBanner from '@/components/common/StickySideBanner'
import Button from '@/components/ui/Button'
import Spinner from '@/components/ui/Spinner'
import { cn } from '@/lib/utils'
import { trackCareCalculatorProgress } from '@/lib/analytics'
import { useCalcQuestions, type NhisItem, type ExtraQuestion } from '@/features/carecalc/api'
import { useCareCalculatorStore } from '@/stores/careCalculatorStore'
import SEO from '@/components/common/SEO'

/**
 * 한 화면에 묻는 단위.
 *
 * <p>체크리스트(`/checklist/step`)와 같은 구조다 — 영역 하나가 화면 하나이고
 * 그 안에 항목이 줄줄이 들어간다. 1화면 1문항이던 것을 바꿨다. 52항목을 한 문항씩
 * 물으면 29화면이 되어 어디까지 왔는지 알 수 없다.</p>
 *
 * <p>항목이 많은 영역(행동변화 14)은 화면을 나누되, 영역 이동 UI는 <b>영역 단위로
 * 하나만</b> 둔다. 나눈 조각까지 넣으면 목록이 화면을 덮는다.</p>
 *
 * <p>PC는 좌측 고정 레일 + 본문 2단이다. 모바일은 칩 + 본문 1단. 같은 화면을
 * 폭만 늘리면 1800px 화면에 672px 카드 하나가 떠 있는 꼴이 된다.</p>
 */
type Screen =
  | {
      kind: 'ITEMS'
      areaIdx: number
      areaLabel: string
      items: NhisItem[]
      /** 이 영역이 나뉜 경우 몇 쪽 중 몇 쪽인지 */
      page: number
      pageCount: number
    }
  | { kind: 'EXTRA'; areaIdx: number; areaLabel: string; questions: ExtraQuestion[] }

/** 한 화면에 담을 최대 항목 수 */
const CHUNK = 8

/** 영역별 안내 문구 — 무엇을 기준으로 답해야 하는지 알려준다 */
const AREA_GUIDE: Record<string, string> = {
  '몸 움직이기': '요즘 한 달 동안 대체로 어떠신지 골라주세요.',
  '기억·판단': '해당하면 「예」, 아니면 「아니요」를 골라주세요.',
  '행동 변화': '최근에 이런 모습이 있었는지 골라주세요.',
  '의료 처치': '지금 받고 계신 것만 「예」로 골라주세요.',
  '마비·관절': '양쪽 중 더 불편한 쪽을 기준으로 골라주세요.',
  '추가 확인': '금액을 정확히 계산하는 데 필요한 내용이에요.',
}

export default function CareCalculatorStepPage() {
  const navigate = useNavigate()
  const { mode, step, answers, setAnswer, goToStep, next, prev, complete } =
    useCareCalculatorStore()
  const { data, isLoading } = useCalcQuestions(mode)

  /** 「다음」을 눌렀는데 빈 항목이 있을 때 그 항목을 짚어준다 */
  const [missingCode, setMissingCode] = useState<string | null>(null)
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({})
  /** 이미 보낸 진행률 마일스톤. 뒤로 갔다 오면 같은 지점을 다시 보내게 되므로 기억한다 */
  const sentMilestones = useRef<Set<number>>(new Set())

  const screens = useMemo<Screen[]>(() => {
    if (!data) return []
    const list: Screen[] = []

    data.areas.forEach((area, areaIdx) => {
      const pageCount = Math.ceil(area.items.length / CHUNK)
      for (let i = 0; i < area.items.length; i += CHUNK) {
        list.push({
          kind: 'ITEMS',
          areaIdx,
          areaLabel: area.userLabel,
          items: area.items.slice(i, i + CHUNK),
          page: Math.floor(i / CHUNK) + 1,
          pageCount,
        })
      }
    })

    if (data.extraQuestions.length > 0) {
      list.push({
        kind: 'EXTRA',
        areaIdx: data.areas.length,
        areaLabel: '추가 확인',
        questions: data.extraQuestions,
      })
    }

    return list
  }, [data])

  /** 영역 목록 — 좌측 레일과 모바일 칩이 함께 쓴다 */
  const areaNav = useMemo(() => {
    if (!data) return []
    const list = data.areas.map((area, areaIdx) => ({
      label: area.userLabel,
      areaIdx,
      codes: area.items.map((i) => i.code),
      firstScreen: screens.findIndex((s) => s.areaIdx === areaIdx),
    }))
    if (data.extraQuestions.length > 0) {
      list.push({
        label: '추가 확인',
        areaIdx: data.areas.length,
        codes: data.extraQuestions.map((q) => q.code),
        firstScreen: screens.findIndex((s) => s.areaIdx === data.areas.length),
      })
    }
    return list
  }, [data, screens])

  if (isLoading || !data || screens.length === 0) {
    return (
      <>
        {/* 진행 중 화면이라 색인 가치가 없다. 랜딩(/care-calculator)으로 신호를 모은다 */}
        <SEO title="돌봄지원 계산기 문답" url="/care-calculator/step" noindex />
        <PageContainer>
          <div className="flex justify-center py-20">
            <Spinner size="lg" />
          </div>
        </PageContainer>
      </>
    )
  }

  const index = Math.min(step, screens.length - 1)
  const screen = screens[index]!
  const isLast = index === screens.length - 1
  const isFirst = index === 0

  /** 이 화면에서 반드시 답해야 하는 항목 — 척도는 필수, 예/아니오는 미선택도 답(아니요) */
  const requiredCodes =
    screen.kind === 'EXTRA'
      ? screen.questions.map((q) => q.code)
      : screen.items.filter((i) => i.scale !== 'YES_NO').map((i) => i.code)
  const firstMissing = requiredCodes.find((code) => answers[code] == null)

  /**
   * 25/50/75% 를 지날 때 한 번씩만 보낸다.
   *
   * 문답이 단일 URL 이라 페이지뷰로는 어디서 멈췄는지 알 수 없다. 이 지점들이 있어야
   * GA4 에서 시작→25→50→75→완료 퍼널이 그려진다. 답변 내용은 보내지 않는다.
   */
  const reportProgress = () => {
    const answered = screens
      .flatMap<string>((sc) =>
        sc.kind === 'ITEMS' ? sc.items.map((i) => i.code) : sc.questions.map((q) => q.code),
      )
      .filter((code) => answers[code] != null).length
    const total = data.totalItems + data.extraQuestions.length
    if (total === 0) return
    const pct = (answered / total) * 100
    for (const milestone of [25, 50, 75] as const) {
      if (pct >= milestone && !sentMilestones.current.has(milestone)) {
        sentMilestones.current.add(milestone)
        trackCareCalculatorProgress(mode, milestone)
      }
    }
  }

  const handleNext = () => {
    if (firstMissing) {
      // 비활성 버튼으로 막고 끝내면 무엇이 빠졌는지 찾아 헤매게 된다.
      // 빠진 항목으로 데려가서 짚어준다.
      setMissingCode(firstMissing)
      itemRefs.current[firstMissing]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    setMissingCode(null)

    // 고르지 않은 예/아니오 항목은 "아니요(0)"로 확정한다.
    // 미응답으로 두면 채점에서 분모가 줄어 점수가 왜곡된다.
    if (screen.kind === 'ITEMS') {
      screen.items
        .filter((i) => i.scale === 'YES_NO')
        .forEach((i) => {
          if (answers[i.code] == null) setAnswer(i.code, '0')
        })
    }
    if (isLast) {
      complete()
      navigate('/care-calculator/result')
      return
    }
    reportProgress()
    next(screens.length - 1)
    window.scrollTo({ top: 0 })
  }

  const handlePrev = () => {
    if (isFirst) {
      navigate('/care-calculator')
      return
    }
    setMissingCode(null)
    prev()
    window.scrollTo({ top: 0 })
  }

  const jumpTo = (screenIdx: number) => {
    setMissingCode(null)
    goToStep(screenIdx)
    window.scrollTo({ top: 0 })
  }

  const answeredTotal = screens
    .flatMap<string>((s) =>
      s.kind === 'ITEMS' ? s.items.map((i) => i.code) : s.questions.map((q) => q.code),
    )
    .filter((code) => answers[code] != null).length
  const totalCount = data.totalItems + data.extraQuestions.length
  const progressPct = Math.round((answeredTotal / totalCount) * 100)

  const rows: { code: string; title: string; help: string | null; options: { label: string; value: string }[]; columns: 2 | 4 }[] =
    screen.kind === 'ITEMS'
      ? screen.items.map((item) => ({
          code: item.code,
          title: item.question,
          help: item.help,
          columns: item.scale === 'YES_NO' ? 2 : 4,
          options: [
            ...item.options.map((label, score) => ({ label, value: String(score) })),
            ...(item.scale === 'YES_NO'
              ? []
              : [{ label: '잘 모르겠어요', value: data.unknownValue }]),
          ],
        }))
      : screen.questions.map((q) => ({
          code: q.code,
          title: q.question,
          help: q.help,
          columns: 2 as const,
          options: q.options,
        }))

  return (
    <>
      {/* 좌우 세로배너 160x600 — 이 화면은 콘텐츠가 max-w-5xl 이라 2xl 이상에서만 자리가 난다.
          (512+160+48(여백) = 720) */}
      <StickySideBanner slotKey="carecalc_side_left" side="left" topPx={96} edgeOffsetPx={720} breakpoint="2xl" />
      <StickySideBanner slotKey="carecalc_side_right" side="right" topPx={96} edgeOffsetPx={720} breakpoint="2xl" />
      {/* 로딩 분기에도 같은 태그가 있다. 둘 중 무엇이 그려져도 noindex 가 유지돼야 한다 */}
      <SEO title="돌봄지원 계산기 문답" url="/care-calculator/step" noindex />

      {/* PageContainer 기본 상한은 480px(모바일 최적화)이라 PC 2단을 담지 못한다 */}
      <PageContainer noMaxWidth>
        <div className="mx-auto max-w-5xl py-5 lg:grid lg:grid-cols-[15rem_minmax(0,1fr)] lg:gap-10 lg:py-8">
          {/* ── 좌측 레일 (PC) ── */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <p className="text-body-sm text-cb-gray-600">
                {totalCount}개 중 <span className="font-bold text-cb-gray-900">{answeredTotal}개</span>{' '}
                답하셨어요
              </p>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-cb-gray-100">
                <div
                  className="h-full rounded-full bg-cb-primary transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>

              <nav className="mt-5 flex flex-col gap-0.5" aria-label="영역 이동">
                {areaNav.map((area) => {
                  const done = area.codes.filter((c) => answers[c] != null).length
                  const isCurrent = area.areaIdx === screen.areaIdx
                  const isComplete = done === area.codes.length
                  return (
                    <button
                      key={area.label}
                      type="button"
                      onClick={() => jumpTo(area.firstScreen)}
                      aria-current={isCurrent ? 'step' : undefined}
                      className={cn(
                        'flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-left transition-colors',
                        isCurrent ? 'bg-cb-primary-light' : 'hover:bg-cb-gray-50',
                      )}
                    >
                      <StepMark
                        n={area.areaIdx + 1}
                        complete={isComplete}
                        current={isCurrent}
                      />
                      <span className="flex-1">
                        <span
                          className={cn(
                            'block text-body-sm font-semibold',
                            isCurrent ? 'text-cb-primary-active' : 'text-cb-gray-700',
                          )}
                        >
                          {area.label}
                        </span>
                        <span className="block text-caption tabular-nums text-cb-gray-500">
                          {done} / {area.codes.length}
                        </span>
                      </span>
                    </button>
                  )
                })}
              </nav>
            </div>
          </aside>

          {/* ── 본문 ── */}
          <div>
            {/* 모바일 진행 상황 */}
            <div className="mb-5 lg:hidden">
              <div className="mb-2 flex items-center justify-between text-body-sm text-cb-gray-600">
                <span>{totalCount}개 중 {answeredTotal}개 답하셨어요</span>
                <span className="tabular-nums">{progressPct}%</span>
              </div>
              <div
                className="h-2 overflow-hidden rounded-full bg-cb-gray-100"
                role="progressbar"
                aria-valuenow={answeredTotal}
                aria-valuemin={0}
                aria-valuemax={totalCount}
                aria-label={`${totalCount}개 중 ${answeredTotal}개 응답`}
              >
                <div
                  className="h-full rounded-full bg-cb-primary transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>

              <div className="mt-3 flex gap-1.5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {areaNav.map((area) => (
                  <button
                    key={area.label}
                    type="button"
                    onClick={() => jumpTo(area.firstScreen)}
                    className={cn(
                      'flex-shrink-0 rounded-full px-3 py-1.5 text-body-sm font-medium transition-colors',
                      area.areaIdx === screen.areaIdx
                        ? 'bg-cb-primary text-white'
                        : area.codes.every((c) => answers[c] != null)
                          ? 'bg-cb-primary/10 text-cb-primary-active'
                          : 'bg-cb-gray-100 text-cb-gray-600',
                    )}
                  >
                    {area.label}
                  </button>
                ))}
              </div>
            </div>

            {/* 영역 제목 */}
            <div className="mb-4">
              <p className="text-body-sm font-medium text-cb-primary-active">
                {screen.areaIdx + 1} / {areaNav.length}
                {screen.kind === 'ITEMS' && screen.pageCount > 1 && (
                  <span className="text-cb-gray-600">
                    {' '}
                    · {screen.page}쪽 / {screen.pageCount}쪽
                  </span>
                )}
              </p>
              <h2 className="mt-0.5 text-h2 font-bold text-cb-gray-900">{screen.areaLabel}</h2>
              <p className="mt-1 text-body leading-relaxed text-cb-gray-600">
                {AREA_GUIDE[screen.areaLabel] ?? '각 항목을 골라주세요.'}
              </p>
            </div>

            {/* 항목 목록 — 카드 안에 카드를 겹치지 않고 구분선으로 나눈다 */}
            <div className="divide-y divide-cb-gray-100 overflow-hidden rounded-2xl border border-cb-gray-200 bg-white">
              {rows.map((row, i) => (
                <ItemRow
                  key={row.code}
                  rowRef={(el) => {
                    itemRefs.current[row.code] = el
                  }}
                  n={i + 1}
                  title={row.title}
                  help={row.help}
                  answered={answers[row.code] != null}
                  missing={missingCode === row.code}
                >
                  <OptionGrid
                    columns={row.columns}
                    options={row.options}
                    selected={answers[row.code]}
                    onSelect={(v) => {
                      setAnswer(row.code, v)
                      if (missingCode === row.code) setMissingCode(null)
                    }}
                    label={row.title}
                  />
                </ItemRow>
              ))}
            </div>

            <div className="mt-6 flex items-center gap-3 lg:justify-between">
              <Button
                variant="ghost"
                size="lg"
                onClick={handlePrev}
                className="flex items-center justify-center gap-1"
              >
                <ArrowLeft size={18} />
                <span>{isFirst ? '나가기' : '이전'}</span>
              </Button>
              <Button
                size="lg"
                onClick={handleNext}
                className="flex flex-1 items-center justify-center gap-1 lg:flex-none lg:min-w-[13rem]"
              >
                <span>
                  {isLast
                    ? '결과 보기'
                    : screen.kind === 'ITEMS' && screen.page < screen.pageCount
                      ? '이어서'
                      : '다음'}
                </span>
                <ArrowRight size={18} />
              </Button>
            </div>

            {missingCode && (
              <p role="alert" className="mt-3 text-body-sm font-medium text-cb-danger">
                아직 고르지 않은 항목이 있어요
              </p>
            )}
          </div>
        </div>
      </PageContainer>
    </>
  )
}

/** 좌측 레일의 단계 표시 — 번호가 완료되면 체크로 바뀐다 */
function StepMark({ n, complete, current }: { n: number; complete: boolean; current: boolean }) {
  return (
    <span
      className={cn(
        'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-caption font-bold',
        complete
          ? 'bg-cb-primary text-white'
          : current
            ? 'bg-white text-cb-primary-active ring-2 ring-cb-primary'
            : 'bg-cb-gray-100 text-cb-gray-600',
      )}
      aria-hidden="true"
    >
      {complete ? <Check size={16} strokeWidth={3} /> : n}
    </span>
  )
}

/**
 * 항목 한 줄.
 *
 * <p>앞머리는 <b>번호</b>다. 예전에 쓰던 빈 동그라미는 라디오처럼 보이는데 눌리지 않아
 * 누르려다 실패하게 만든다. 답하면 번호가 체크로 바뀐다.</p>
 */
const ItemRow = ({
  rowRef,
  n,
  title,
  help,
  answered,
  missing,
  children,
}: {
  /** `ref` 라는 이름은 React 18에서 예약어라 일반 prop 으로 전달되지 않는다 */
  rowRef: (el: HTMLDivElement | null) => void
  n: number
  title: string
  help: string | null
  answered: boolean
  missing: boolean
  children: React.ReactNode
}) => (
  <div
    ref={rowRef}
    className={cn(
      'scroll-mt-24 px-4 py-4 transition-colors sm:px-5',
      missing && 'bg-cb-danger/5',
    )}
  >
    <div className="mb-3 flex items-start gap-3">
      <span
        className={cn(
          'mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-caption font-bold',
          answered
            ? 'bg-cb-primary text-white'
            : missing
              ? 'bg-cb-danger text-white'
              : 'bg-cb-gray-100 text-cb-gray-600',
        )}
        aria-hidden="true"
      >
        {answered ? <Check size={14} strokeWidth={3} /> : n}
      </span>
      <div>
        <p className="text-[1.0625rem] font-semibold leading-snug text-cb-gray-900">{title}</p>
        {help && (
          <p className="mt-1 text-[0.9375rem] leading-relaxed text-cb-gray-600">{help}</p>
        )}
      </div>
    </div>
    <div className="pl-9">{children}</div>
  </div>
)

/**
 * 선택지 버튼 묶음.
 *
 * <p>4열은 <b>lg 이상에서만</b> 쓴다. 「전부 해드려야 해요」를 좁은 4열에 넣으면
 * 「전부 해드려 / 야 해요」처럼 단어 중간에서 끊긴다 (영구지침 §8-3).</p>
 */
function OptionGrid({
  columns,
  options,
  selected,
  onSelect,
  label,
}: {
  columns: 2 | 4
  options: { label: string; value: string }[]
  selected: string | undefined
  onSelect: (value: string) => void
  label: string
}) {
  return (
    <div
      className={cn(
        'grid gap-2',
        // 「예/아니요」 두 개를 본문 폭 전체로 늘리면 두 글자짜리 버튼이 300px 이 된다
        columns === 2 ? 'grid-cols-2 sm:max-w-md' : 'grid-cols-2 lg:grid-cols-4',
      )}
      role="radiogroup"
      aria-label={label}
    >
      {options.map((opt) => {
        const isSelected = selected === opt.value
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => onSelect(opt.value)}
            className={cn(
              'flex min-h-[3rem] items-center justify-center whitespace-nowrap rounded-lg px-2 py-2.5 text-center text-[0.9375rem] font-semibold transition-colors',
              isSelected
                ? 'bg-cb-primary text-white'
                : 'bg-white text-cb-gray-700 ring-1 ring-cb-gray-200 hover:bg-cb-gray-50',
            )}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}
